import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useLang } from '../contexts/LangContext';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Search, BookOpen, ArrowRight, Clock } from 'lucide-react';
import { PageSEO } from '../components/PageSEO';
import { CourseSkeleton } from '../components/Skeletons';

const TRACK_COLORS = {
  'AI': '#D4A843',
  'Fintech': '#1B93A4',
  'Economics': '#FF6B6B',
  'Futurism': '#D4A843',
  'Business': '#1B93A4',
  'Resource Intelligence': '#FF6B6B',
  'Quantum': '#D4A843',
  'Data': '#1B93A4',
  'Career': '#FF6B6B',
  'Diaspora': '#D4A843',
  'Fundraise': '#1B93A4',
  'Leadership': '#FF6B6B',
  'Digital Safety': '#D4A843',
  'Capstone': '#1B93A4',
  'Strategy': '#FF6B6B',
  'RealEstate': '#D4A843',
  'Sports': '#1B93A4'
};

const TIER_CONFIG = {
  'Beginner': { label: 'Foundation', colour: '#1B93A4', tier: 1, bg: 'bg-emerald-900/30', text: 'text-emerald-300' },
  'Beginner-Intermediate': { label: 'Foundation', colour: '#1B93A4', tier: 1, bg: 'bg-emerald-900/30', text: 'text-emerald-300' },
  'Foundation': { label: 'Foundation', colour: '#1B93A4', tier: 1, bg: 'bg-emerald-900/30', text: 'text-emerald-300' },
  'Intermediate': { label: 'Applied', colour: '#D4A843', tier: 2, bg: 'bg-amber-900/30', text: 'text-amber-300' },
  'Applied': { label: 'Applied', colour: '#D4A843', tier: 2, bg: 'bg-amber-900/30', text: 'text-amber-300' },
  'Intermediate-Advanced': { label: 'Applied', colour: '#D4A843', tier: 2, bg: 'bg-amber-900/30', text: 'text-amber-300' },
  'Advanced': { label: 'Strategic', colour: '#FF6B6B', tier: 3, bg: 'bg-purple-900/30', text: 'text-purple-300' },
  'Strategic': { label: 'Strategic', colour: '#FF6B6B', tier: 3, bg: 'bg-purple-900/30', text: 'text-purple-300' },
  'Beginner-Advanced': { label: 'Applied', colour: '#D4A843', tier: 2, bg: 'bg-amber-900/30', text: 'text-amber-300' },
};

const LEVEL_PILL = {
  'Foundation': 'bg-emerald-100 text-emerald-800',
  'Applied': 'bg-amber-100 text-amber-800',
  'Strategic': 'bg-purple-100 text-purple-800',
};

export default function CoursesPage() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [activeTrack, setActiveTrack] = useState('All');
  const [activeTier, setActiveTier] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const { data } = await api.get(`/courses?lang=${lang}`, { signal: controller.signal });
        setCourses(data.courses || []);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [lang]);

  const tracks = ['All', ...new Set(courses.map(c => c.track))];

  const filtered = courses.filter(c => {
    const matchTrack = activeTrack === 'All' || c.track === activeTrack;
    const matchSearch = !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.description.toLowerCase().includes(search.toLowerCase());
    const tier = TIER_CONFIG[c.level] || { tier: 1 };
    const matchTier = activeTier === 'All' || (activeTier === 'Foundation' && tier.tier === 1) || (activeTier === 'Applied' && tier.tier === 2) || (activeTier === 'Strategic' && tier.tier === 3);
    return matchTrack && matchSearch && matchTier;
  });

  return (
    <div data-testid="courses-page" className="min-h-screen bg-[#F8FAFC]">
      <PageSEO title="Courses" description="Browse free courses across AI, fintech, economics, and futurism. All courses are free, designed for self-paced learning." path="/courses" />
      {/* Header */}
      <div className="bg-[#0B1D3A] px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-['Outfit'] font-bold text-2xl sm:text-3xl text-white mb-2">{t('courses.catalog')}</h1>
          <p className="font-['Manrope'] text-white/40 text-sm">
            {courses.length} {t('courses.coursesAcross')} {new Set(courses.map(c => c.track)).size} {t('courses.tracks')}
          </p>

          <div className="relative mt-6 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
            <Input
              data-testid="course-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('courses.search')}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#D4A843] focus:ring-1 focus:ring-[#D4A843] rounded-xl"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Tier filters */}
        <div className="flex gap-2 mb-4">
          {['All', 'Foundation', 'Applied', 'Strategic'].map(tier => {
            const tierColours = { Foundation: '#1B93A4', Applied: '#D4A843', Strategic: '#FF6B6B' };
            return (
              <button
                key={tier}
                data-testid={`tier-filter-${tier}`}
                onClick={() => setActiveTier(tier)}
                className={`px-4 py-2 rounded-full text-xs font-['Manrope'] font-semibold whitespace-nowrap transition-colors duration-200 ${
                  activeTier === tier
                    ? 'text-white shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
                style={activeTier === tier ? { backgroundColor: tierColours[tier] || '#0B1D3A' } : {}}
              >
                {tier === 'All' ? 'All Levels' : tier}
              </button>
            );
          })}
        </div>

        {/* Track filters */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
          {tracks.map(track => (
            <button
              key={track}
              data-testid={`track-filter-${track}`}
              onClick={() => setActiveTrack(track)}
              className={`px-4 py-2 rounded-full text-xs font-['Manrope'] font-medium whitespace-nowrap transition-colors duration-200 ${
                activeTrack === track
                  ? 'bg-[#0B1D3A] text-white shadow-sm'
                  : 'bg-white border border-gray-200 text-gray-500 hover:border-[#D4A843]/30 hover:text-gray-700'
              }`}
            >
              {track}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1,2,3,4,5,6].map(i => <CourseSkeleton key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="font-['Manrope'] text-gray-400 text-sm">{t('courses.noCourses')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(course => {
              const tier = TIER_CONFIG[course.level] || TIER_CONFIG['Beginner'];
              const levelLabel = tier.label;
              const pillClass = LEVEL_PILL[levelLabel] || LEVEL_PILL['Foundation'];
              const hours = course.estimated_hours || (course.modules ? Math.ceil(course.modules.length * 3) : null);
              const outcome = course.learning_outcome;

              return (
                <button
                  key={course.id}
                  data-testid={`course-card-${course.id}`}
                  onClick={() => navigate(`/courses/${course.id}`)}
                  className="relative text-left bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
                >
                  {/* Level pill — top right */}
                  {levelLabel && (
                    <span className={`absolute top-3 right-3 text-xs font-medium rounded-full px-2.5 py-1 ${pillClass}`}>
                      {levelLabel}
                    </span>
                  )}

                  <div className="flex items-center gap-2 mb-3 pr-20">
                    <Badge
                      className="text-xs border-0 rounded-lg"
                      style={{ backgroundColor: `${TRACK_COLORS[course.track] || '#D4A843'}15`, color: TRACK_COLORS[course.track] || '#D4A843' }}
                    >
                      {course.track}
                    </Badge>
                  </div>

                  <h3 className="font-['Outfit'] font-semibold text-[#0B1D3A] text-base mb-2 group-hover:text-[#D4A843] transition-colors duration-200">{course.title}</h3>
                  <p className="font-['Manrope'] text-gray-400 text-sm line-clamp-2 mb-2">{course.description}</p>

                  {/* Learning outcome */}
                  {outcome && (
                    <p className="font-['Manrope'] text-sm mb-3 line-clamp-1">
                      <span className="font-semibold text-[#0B1D3A]/70">{t('courses.youllBeAbleTo')} </span>
                      <span className="text-gray-400">{outcome}</span>
                    </p>
                  )}

                  {/* Metadata row */}
                  <div className="flex items-center gap-3 mb-3">
                    {hours && (
                      <span className="flex items-center gap-1 font-['Manrope'] text-gray-400 text-xs">
                        <Clock className="w-3 h-3" /> ~ {hours} hours
                      </span>
                    )}
                    {course.modules && (
                      <span className="font-['Manrope'] text-gray-400 text-xs">
                        {course.modules.length} modules
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1 text-[#D4A843] text-sm font-['Manrope'] font-medium">
                    {t('courses.viewCourse')} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
